import React from 'react';
import { Button, ButtonGroup, Menu, MenuItem, ListItemIcon, useTheme } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ProgressButton, { ProgressStatus } from './ProgressButton';
import { menueFarbe } from './menueFarbe';
import { TOUCH_TARGET_COARSE_QUADRAT } from '../../theme/tokens';

const GROBER_ZEIGER = '@media (pointer: coarse)';

/**
 * Der Pfeil haelt auf grobem Zeiger BEIDE Achsen auf 44 — dieselbe Hausform wie
 * der ⋯-Knopf des `ViewHeader` (`MuiIconButton`, `theme.js`).
 *
 * 🔴 `width` ZUSÄTZLICH zu `minWidth`: MUI's `ButtonGroup` gibt seinen
 * gruppierten Kindern eine eigene Mindestbreite — hergeleitet rund 40 px
 * (R16 W-3, **ungemessen**: kein Browser, kein `node_modules`) —, und ein
 * `minWidth` aus `sx` steht dagegen auf der Verliererseite der Kaskade
 * (niedrigere Spezifitaet). `width` bleibt davon unberuehrt und ergibt denselben
 * 44er-Kasten. Beides haengt an `pointer: coarse` — der Desktop ist unveraendert.
 *
 * 🔴 W-3 (Wiedervorlage 2, 2026-10-07): der Breitenwert kommt aus dem TOKEN
 * (`TOUCH_TARGET_COARSE_QUADRAT[…].minWidth`), nicht als zweites Literal daneben
 * — zwei Zahlen, die heute gleich sind, koennen auseinanderlaufen.
 */
const PFEIL_GROBER_ZEIGER = {
  [GROBER_ZEIGER]: {
    ...TOUCH_TARGET_COARSE_QUADRAT[GROBER_ZEIGER],
    width: TOUCH_TARGET_COARSE_QUADRAT[GROBER_ZEIGER].minWidth,
  },
};

// 🔴 B1 (Regel 15/16, 2026-10-08): ein Menueeintrag darf `color` tragen — bis
// zum 2026-10-08 warf die `sx`-Zeile unten `m.color` weg, und die zerstoerende
// Haelfte des Paares (`ScraperList`, „Deduplicate") verlor auf dem Desktop ihre
// Warnfarbe, waehrend der mobile ⋯-Ueberlauf sie behielt.
//
// 🔴 E3 (UI-Durchgang 2 Teil 4, 2026-10-08): die Abbildung liegt seit dieser
// Runde EINMAL im Baum, in `./menueFarbe`. Bis dahin stand sie hier und in
// `ViewHeader.jsx` (`ueberlaufFarbe`) — begruendet mit einem Zyklus, der so
// nicht existiert: die einzige Kante ist `ViewHeader → SplitButton`, und ein
// drittes Modul, das beide importieren, liegt auf keiner Kante. Die zwei
// Fassungen sind damit auch inhaltlich eine: der Zielpfad ist nicht mehr
// `<schluessel>.main`, sondern der TEXT-TON der v2-Skala (Begruendung und
// Kontrastzahlen im Kopf von `menueFarbe.js`).

/**
 * SplitButton — eine Hauptaktion mit angehängtem Untermenü.
 *
 * @description
 *   Zwei Hälften in EINEM `ButtonGroup`: links die Hauptaktion (ein Klick
 *   führt sie aus), rechts ein Pfeil-Knopf, der ein `Menu` mit den
 *   Geschwister-Aktionen öffnet. Gebaut für Kopfleisten, in denen mehrere
 *   gleichartige Aktionen um denselben Gegenstand kreisen („Sync products" ▾
 *   „Sync market prices") und als drei Einzelknöpfe nebeneinander keine
 *   Rangfolge mehr erkennen liessen.
 *
 *   Die Hauptaktion ist optional ein Fortschrittsknopf: mit gesetztem
 *   `running` rendert die linke Hälfte `ProgressButton` und zeigt den
 *   Fortschritt im Knopf selbst.
 *
 *   🔴 Beide Hälften sind EIN Bedienelement: `running` oder `disabled`
 *   sperren die linke Hälfte UND den Pfeil. Ein halb gesperrter Split-Button
 *   liesse das Menü offen, während die Hauptaktion läuft.
 *
 *   🔴 **Der Ladezustand steht an der linken Hälfte, nicht nur in ihrem
 *   Aussehen** (K-F1, 2026-10-10). Mit gesetztem `running` ist die linke Hälfte
 *   ein `ProgressButton`, und der trägt `aria-busy="true"`, solange er läuft —
 *   dieser Baustein setzt das Attribut deshalb NICHT selbst; er würde damit nur
 *   eine zweite Wahrheit neben die erste stellen. Festgehalten ist es in
 *   `SplitButton.test.jsx` (linke Hälfte trägt `aria-busy` während `running`,
 *   der Pfeil nicht), damit die Zusage nicht mit dem nächsten Umbau still
 *   verschwindet.
 *
 *   🔴 **Die Live-Region des Fortschrittstextes steht AUSSERHALB der Gruppe**
 *   (R16 B1, 2026-10-10). `ProgressButton` legt sie als Geschwister ab, weil
 *   `button` laut ARIA 1.2 `Children Presentational: True` trägt; hier wäre ein
 *   zweites Kind aber kein Knopf mehr, und die `ButtonGroup` vergäbe ihre
 *   Klassen an ein Fragment. Die linke Hälfte bekommt deshalb
 *   `statusRegion={false}` und dieser Baustein setzt die Region selbst, direkt
 *   hinter die Gruppe.
 *
 * @useWhen
 *   - Eine Hauptaktion hat ein bis drei seltenere Geschwister derselben Art
 *   - Der Fortschritt gehört in den Knopf selbst („Syncing 42 / 150")
 *   - Eine Kopfleiste soll EINE gefüllte Aktion zeigen, nicht drei
 *   - Eine der Geschwister-Aktionen ist zerstörend und gehört in die Warnfarbe
 *     (`menu: [{ …, color: 'warning' }]`) — sie steht dann im Menü statt als
 *     lauteste Fläche der Seite neben dem Zeiger
 *
 * @dontUseFor
 *   - Unabhängige Aktionen ohne gemeinsamen Gegenstand — nimm einzelne
 *     `Button`s oder den `overflow`-Slot des `ViewHeader`
 *   - Reine Navigation — ein `Button` genügt
 *   - Mehr als ~5 Untereinträge — dann ist es eine eigene Fläche
 *   - **Neben einem ⋯** — ein Seitenkopf zeigt höchstens EIN Menü (KM-1,
 *     2026-10-10): dort rendert der `ViewHeader` die Aktion als einfachen Knopf
 *     und stellt ihre Varianten oben in den ⋯. Ein `SplitButton` NEBEN einem
 *     ⋯-Knopf ist genau das Pattern, das die Regel abschafft
 *
 * @related
 *   - ProgressButton — die linke Hälfte, wenn `running` gesetzt ist
 *   - ViewHeader — der Aufrufer; `actions` mit `menu: [...]` rendert diesen
 *     Baustein **nur, wenn der Kopf sonst keine ⋯-Einträge hat** (KM-1),
 *     `overflow` rendert ein `Menu` am ⋯-Knopf
 *
 * @reuseHints
 *   - „Export ▾ (CSV | XLSX)" in Berichts-Kopfleisten
 *   - „Send ▾ (Now | Scheduled)" in Bestell-Details
 *
 * @props
 *   - label — Beschriftung der Hauptaktion (auch `aria-label` der Gruppe)
 *   - onClick — Klick auf die Hauptaktion
 *   - menu — Array<{ key?, label, onClick, disabled?, icon?, color? }>; leer = kein
 *     Pfeil nötig. `color` färbt den Menüeintrag: ein nackter Palettenschlüssel
 *     (`'warning'`, wird auf den Textton `warn` der v2-Skala gezogen) oder ein
 *     fertiger Pfad (`'error.main'`, geht unverändert durch) — dieselbe
 *     Abbildung, die der `ViewHeader` auf seine Überlauf-Einträge anwendet
 *     (`./menueFarbe`, EIN Modul für beide: `ViewHeader` importiert diesen
 *     Baustein, ein drittes Modul liegt auf keiner Kante)
 *   - variant — MUI-Variante beider Hälften (Vorgabe `contained`)
 *   - color — MUI-Farbe beider Hälften (Vorgabe `primary`)
 *   - startIcon — Zeichen links in der Hauptaktion
 *   - disabled — sperrt beide Hälften
 *   - running — true ⇒ linke Hälfte wird `ProgressButton` (und trägt dann
 *     `aria-busy`), beide Hälften sperren
 *   - progress — 0..1 für den Füllstand, oder null für unbestimmt
 *   - size — MUI-Größe (Vorgabe `small`, die Hausgröße)
 */
export default function SplitButton({
  label,
  onClick,
  menu = [],
  variant = 'contained',
  color = 'primary',
  startIcon = null,
  disabled = false,
  running,
  progress = null,
  size = 'small',
}) {
  const theme = useTheme();
  const [anchor, setAnchor] = React.useState(null);
  const offen = Boolean(anchor);
  const gesperrt = disabled || running;

  // Gemeinsame Props der linken Hälfte. `disabled` steuert ProgressButton
  // selbst (`disabled || running`) — hier steht dieselbe Summe, damit beide
  // Zweige denselben Zustand tragen.
  const hauptProps = {
    size,
    variant,
    color,
    startIcon,
    onClick,
    disabled: gesperrt,
  };

  return (
    <>
      <ButtonGroup
        variant={variant}
        color={color}
        size={size}
        disabled={gesperrt}
        aria-label={label}
      >
        {running === undefined ? (
          <Button {...hauptProps}>{label}</Button>
        ) : (
          // 🔴 `statusRegion={false}` (R16 B1, 2026-10-10): die Live-Region des
          // `ProgressButton` steht sonst als GESCHWISTER des Knopfes — und hier
          // waere das ein zweites Kind der `ButtonGroup`. MUI klont seine
          // Gruppenkinder und vergaebe die Gruppenklassen (Randrundung der
          // linken Haelfte) an ein Fragment, das sie nicht traegt. Die Region
          // steht deshalb unten AUSSERHALB der Gruppe, ueber `ProgressStatus` —
          // derselbe Baustein, derselbe Text, nur ein anderer Ort.
          <ProgressButton
            {...hauptProps}
            running={running}
            progress={progress}
            label={label}
            statusRegion={false}
          />
        )}
        {/* Der Pfeil trägt seinen eigenen Namen — ohne ihn meldet ein
            Screenreader nur „button", und `getByRole('button', { name })`
            findet ihn nicht. `aria-expanded` spiegelt das Menü.
            🔴 W-3 (Regel 16, 2026-10-07): `sx` gibt ihm auf grobem Zeiger die
            44 x 44 des ⋯-Knopfes daneben; vorher standen dort zwei
            Bedienelemente derselben Zeile mit verschiedener Trefferbreite. */}
        <Button
          size={size}
          sx={PFEIL_GROBER_ZEIGER}
          aria-haspopup="menu"
          aria-expanded={offen}
          aria-label={`More ${label} options`}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <ArrowDropDownIcon fontSize="small" />
        </Button>
      </ButtonGroup>
      {/* Die Live-Region der linken Haelfte — AUSSERHALB der Gruppe, weil sie
          als Kind die Gruppenklassen verloere (Begruendung an `ProgressButton`
          oben). Nur im Fortschrittsfall: ein gewoehnlicher `Button` links hat
          keinen laufenden Text anzusagen. */}
      {running !== undefined && <ProgressStatus text={running ? label : ''} />}
      <Menu anchorEl={anchor} open={offen} onClose={() => setAnchor(null)}>
        {menu.map((m, idx) => (
          <MenuItem
            key={m.key ?? idx}
            disabled={m.disabled}
            onClick={() => { setAnchor(null); m.onClick && m.onClick(); }}
            // 🔴 B1: die Farbe des Eintrags geht MIT — bis zum 2026-10-08 warf
            // diese Zeile `m.color` weg, und die zerstoerende Haelfte eines
            // Paares verlor auf dem Desktop ihre Warnfarbe, waehrend der mobile
            // Ueberlauf sie behielt (`ViewHeader.jsx:818`).
            sx={m.color ? { color: menueFarbe(m.color, theme) } : undefined}
          >
            {m.icon ? <ListItemIcon>{m.icon}</ListItemIcon> : null}
            {m.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
